/**
 * Shared attachment rules for Activity and Order attachments.
 *
 * These mirror the backend configuration (`app.attachments.*` in application.yml),
 * which uses a single generic upload path for both entity types.
 */

export const MAX_ATTACHMENT_FILES = 10
export const MAX_ATTACHMENT_FILE_SIZE = 80 * 1024 * 1024 // 80 MB per file

/**
 * MIME map for the `accept` prop of Chakra's FileUpload / the native file input.
 */
export const ATTACHMENT_ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/plain': ['.txt'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'application/zip': ['.zip'],
  'application/x-7z-compressed': ['.7z'],
  'application/x-tar': ['.tar'],
}

/**
 * Allowed file extensions (lowercase, leading dot).
 *
 * Validation is done by extension rather than MIME type on purpose: browsers report
 * inconsistent MIME types for the same file depending on the OS (e.g. Windows reports
 * `.zip` as `application/x-zip-compressed` instead of `application/zip`), so a
 * MIME-based check would reject legitimately supported files.
 */
export const ALLOWED_ATTACHMENT_EXTENSIONS: string[] = Object.values(ATTACHMENT_ACCEPT).flat()

/** Value for a native `<input type="file" accept="...">`. */
export const ATTACHMENT_ACCEPT_ATTR = [
  ...Object.keys(ATTACHMENT_ACCEPT),
  ...ALLOWED_ATTACHMENT_EXTENSIONS,
].join(',')

export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot < 0) return ''
  return fileName.slice(lastDot).toLowerCase()
}

export function isAllowedAttachment(file: File): boolean {
  return ALLOWED_ATTACHMENT_EXTENSIONS.includes(getFileExtension(file.name))
}

export type AttachmentValidationResult = {
  accepted: File[]
  /** Files rejected because their extension is not supported. */
  invalidType: File[]
  /** Files rejected because they exceed the per-file size limit. */
  tooLarge: File[]
  /** Files rejected because they would exceed the per-entity file count limit. */
  overLimit: File[]
}

/**
 * Splits the incoming files into accepted and rejected buckets.
 *
 * @param files       newly selected files
 * @param existingCount number of files already attached to the entity
 */
export function validateAttachments(files: File[], existingCount = 0): AttachmentValidationResult {
  const result: AttachmentValidationResult = {
    accepted: [],
    invalidType: [],
    tooLarge: [],
    overLimit: [],
  }

  for (const file of files) {
    if (!isAllowedAttachment(file)) {
      result.invalidType.push(file)
      continue
    }
    if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
      result.tooLarge.push(file)
      continue
    }
    if (existingCount + result.accepted.length >= MAX_ATTACHMENT_FILES) {
      result.overLimit.push(file)
      continue
    }
    result.accepted.push(file)
  }

  return result
}

const names = (files: File[]) => files.map(f => f.name).join(', ')

/**
 * Builds a German, user-facing description of why files were rejected.
 * Returns `null` when nothing was rejected.
 */
export function describeRejectedAttachments(result: AttachmentValidationResult): string | null {
  const parts: string[] = []

  if (result.invalidType.length > 0) {
    parts.push(
      `Nicht unterstützter Dateityp: ${names(result.invalidType)}. ` +
        `Erlaubt sind: ${ALLOWED_ATTACHMENT_EXTENSIONS.join(', ')}.`
    )
  }
  if (result.tooLarge.length > 0) {
    parts.push(
      `Zu groß (max. ${Math.round(MAX_ATTACHMENT_FILE_SIZE / (1024 * 1024))} MB pro Datei): ` +
        `${names(result.tooLarge)}.`
    )
  }
  if (result.overLimit.length > 0) {
    parts.push(
      `Es sind höchstens ${MAX_ATTACHMENT_FILES} Dateien erlaubt. ` +
        `Nicht hinzugefügt: ${names(result.overLimit)}.`
    )
  }

  return parts.length > 0 ? parts.join(' ') : null
}

/**
 * Translates the backend's English attachment errors into German.
 *
 * The upload endpoints return plain English text (e.g. "File type not allowed:
 * application/x-zip-compressed"), which would otherwise be shown verbatim to the user.
 * Unrecognised messages are passed through unchanged.
 */
export function translateAttachmentError(message: string): string {
  const text = message.trim()

  const notAllowed = text.match(/^File type not allowed:\s*(.+)$/i)
  if (notAllowed) {
    return (
      `Dieser Dateityp wird nicht unterstützt (${notAllowed[1]}). ` +
      `Erlaubt sind: ${ALLOWED_ATTACHMENT_EXTENSIONS.join(', ')}.`
    )
  }

  const tooLarge = text.match(/^File size exceeds (\d+) MB limit$/i)
  if (tooLarge) {
    return `Die Datei ist zu groß (max. ${tooLarge[1]} MB pro Datei).`
  }

  const totalTooLarge = text.match(/^Total attachment size would exceed (\d+) MB limit$/i)
  if (totalTooLarge) {
    return `Die Anhänge überschreiten insgesamt das Limit von ${totalTooLarge[1]} MB.`
  }

  const tooManyPerRequest = text.match(/^Maximum (\d+) files per request allowed$/i)
  if (tooManyPerRequest) {
    return `Es können höchstens ${tooManyPerRequest[1]} Dateien auf einmal hochgeladen werden.`
  }

  const limitExceeded = text.match(
    /^Attachment limit exceeded: entity already has (\d+) file\(s\)\. Max per entity is (\d+)\. You can upload up to (\d+) more file\(s\)\.$/i
  )
  if (limitExceeded) {
    const [, current, max, remaining] = limitExceeded
    return (
      `Maximale Anzahl an Anhängen erreicht: bereits ${current} von ${max} Dateien vorhanden. ` +
      `Sie können noch ${remaining} Datei(en) hochladen.`
    )
  }

  if (/^File is empty$/i.test(text)) {
    return 'Die Datei ist leer.'
  }

  if (/^Invalid filename$/i.test(text)) {
    return 'Ungültiger Dateiname.'
  }

  if (/^No files provided$/i.test(text)) {
    return 'Es wurde keine Datei ausgewählt.'
  }

  return message
}
